import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { Separator } from "@/components/ui/separator";

const associations = [
  { org: "UNESCO: Greening Education Partnership", desc: "Steering Committee and Working Group member, contributing to Greening Curriculum Guidance and Green School Quality Standards." },
  { org: "Energy Swaraj Foundation: Finite Earth Movement", desc: "Initiated and led a global climate Action initiative with aim to reach 1 billion people; helped establish Climate Action Hubs across 300+ institutions." },
  { org: "Startup Eco Ashram & Build3", desc: "Contributed to building a 30-acre living ecosystem for conscious entrepreneurs and regenerative initiatives." },
  { org: "Bizzseva Consultancy Services LLP", desc: "Founder and Managing Partner. Led a business consultancy supporting startups and growing enterprises." },
  { org: "Harvard University: Aspire Leaders Program (2023)", desc: "Selected participant in a values-driven global leadership program." },
  { org: "Institute of Management Accountants (IMA)", desc: "Former Director, IMA Pune Chapter." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-foreground">
              About Bikram
            </h1>
          </div>
        </FadeIn>
      </section>

      {/* Content */}
      <section className="pb-24 md:pb-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto space-y-20">
          {/* Orientation */}
          <FadeIn>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">
                Orientation & Concern
              </h2>
              <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
                <p>
                  Bikram Jha is engaged in a lifelong inquiry into <strong className="text-foreground font-medium">human consciousness and the roots of suffering</strong>. His work is not based on belief, ideology, or spiritual authority, but on direct observation of life as it unfolds.
                </p>
                <p>At the center of his inquiry is a simple question:</p>
                <blockquote className="font-accent italic text-xl md:text-2xl text-foreground pl-6 border-l-2 border-[#5C6B5D] my-8">
                  Why does human life remain divided, conflicted, and inwardly burdened despite all external progress?
                </blockquote>
                <p>
                  He observes that the psychological sense of "I" creates separation, between self and other, humanity and nature, leading to fear, violence, ecological destruction, and inner distress. What we see in the world reflects this inner fragmentation.
                </p>
              </div>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Awareness */}
          <FadeIn>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">
                Awareness & Transformation
              </h2>
              <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
                <p>
                  For Bikram, <strong className="text-foreground font-medium">awareness is not a practice or method</strong>. It arises naturally when thought, conditioning, fear, and desire are observed without choice. In such observation, mechanical reactions end, and clarity acts.
                </p>
                <p>
                  Inner transformation is not becoming something else. It begins the moment one clearly sees one's conditioning. From this inquiry, Bikram discovered a new way of living, not driven by conflict or becoming, but rooted in awareness and responsibility.
                </p>
              </div>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Journey */}
          <FadeIn>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">
                Journey
              </h2>
              <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
                <p>
                  From childhood, he was curious and questioning everything. During his college years, he felt an emptiness in life, a deep sense of something missing. This led him to turn inward, seeking answers to life's questions and a way to end suffering. Over time, he explored various philosophies and ended up discovering the teachings of J. Krishnamurti.
                </p>
                <p>
                  Through retreats, books, and dialogues, he began to understand himself and gain insights. Later, dialogue with Dharamraj through the Aryanak platform furthered his understanding, which eventually helped him discover a new way of living free from suffering.
                </p>
                <p>
                  Born in a small village in Bihar, Bikram studied across Odisha and Bihar before completing his MBA. He briefly worked in corporate and entrepreneurial spaces, then stepped away, sensing that external success did not address the deeper human crisis.
                </p>
                <p>
                  After his independent enquiry, it is the very first realization that the crises we see in the world (violence, division, and suffering) are mere reflections of the crisis within us. The root of disorder is the 'I': the illusion of separateness, the endless pursuit of power, comparison, and fear.
                </p>
                <p>
                  True transformation is not found in external reforms but in an inner revolution. Only through deep awareness of how the 'I' operates can we dissolve conflict and bring about real change. This understanding and realization became the pillar of <strong className="text-foreground font-medium">Jeevan Darshan Foundation (JDF)</strong> with the grace of existence to address this crisis in human consciousness and nurturing the new way of living free from suffering, misery, slavery, and rooted in love, self-observation, awareness, and oneness.
                </p>
              </div>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Work & Living */}
          <FadeIn>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">
                Work & Living
              </h2>
              <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
                <p>
                  Bikram is the founder of <strong className="text-foreground font-medium">Jeevan Darshan Foundation (JDF)</strong>, working across self-inquiry, education, mental health, ecology, and conscious living.
                </p>
                <p>
                  His initiatives include Life Education Development Program, Self Awareness Program, JDF Community Learning Centers (JCLC), Train the Trainer Program with Art of Listening for JDF mental health Ambassador helping people with unconditional and non-judgmental way of listening, Youth for Self Knowing Program, Climate Advocacy Program, JDF Tapovan Ashram (Upcoming), and more.
                </p>
                <p>
                  He does not have a permanent residence and spends most of his time in Bihar, engaging in dialogue, rural initiatives, and living experiments.
                </p>
              </div>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Way of Engagement */}
          <FadeIn>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground">
                Way of Engagement
              </h2>
              <div className="font-body text-base md:text-lg leading-relaxed text-muted-foreground space-y-5">
                <p>
                  Bikram holds a <strong className="text-foreground font-medium">non-authoritative stance</strong>. He offers no paths or answers, only dialogue. His work invites those who feel the urgency to observe, question, and discover for themselves.
                </p>
              </div>
              <Link
                to="/the-work"
                className="inline-flex items-center gap-2 mt-6 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
                data-testid="about-explore-work"
              >
                Explore The Work <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Associations */}
          <FadeIn>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8 text-foreground">
                Associations & Past Work
              </h2>
              <div className="space-y-6">
                {associations.map((a, i) => (
                  <div key={i} className="font-body" data-testid={`association-${i}`}>
                    <h4 className="text-sm md:text-base font-medium text-foreground mb-1">{a.org}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{a.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} />

          {/* Download Bio */}
          <FadeIn>
            <div className="text-center">
              <button
                className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase px-8 py-3 border border-[#5C6B5D] text-[#5C6B5D] hover:bg-[#5C6B5D] hover:text-white transition-all duration-300"
                data-testid="download-bio-btn"
                onClick={() => alert("Bio PDF will be available soon.")}
              >
                <Download size={14} strokeWidth={1.5} />
                Download Short Bio
              </button>
              <p className="font-body text-xs text-muted-foreground mt-3">PDF coming soon</p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
